import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Address,
  DraftEmailType,
  LabelId,
  RecipientType,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import {
  commitSavedDraftToStores,
  isThreadBoundHandler,
} from './commitSavedDraft';
import { useLoadedDraftVersion } from './useLoadedDraftVersion';
import { SaveErrorToast } from '../../../../../enums/saveErrorToast';
import {
  DraftAccount,
  FileAttachment,
  HandlerButton,
  isDraftConflictResult,
  isPayloadTooLargeResult,
  isSameDraftAccount,
  resolveDraftAccount,
  SendDraftProperties,
  useSaveDraft,
  useSendDraft,
} from '../../../../../hooks';
import { isDraftNotFoundError } from '../../../../../hooks/UserCommunications/draft/useDeleteDraft';
import { useCustomToast } from '../../../../../hooks/useCustomToast';
import {
  useInboxEntriesStore,
  useIntegrationsStore,
  useUserCommunicationsStore,
} from '../../../../../store';
import { DRAFT_AUTO_RETRY_DELAYS_MS } from '../../../../../utils/constants/drafts';
import { VISIBILITY_VISIBLE } from '../../../../../utils/constants/uiConstants';
import {
  normalizeDraftHtmlForComparison,
  validateEditorContent,
} from '../../../../../utils/editorContentUtils';
import { handleSelectDraftReqType } from '../../../../../utils/handleSelectDraftReqType';
import { navigateToInboxOrDoBox } from '../../../../../utils/navigation';

export interface DraftData {
  subject?: string;
  message?: string;
  toAddresses: Address[];
  ccAddresses: Address[];
  bccAddresses: Address[];
  attachments?: FileAttachment[];
  attachmentsToRemove?: string[];
  draftId?: string;
  fromEmail?: string;
  messageId?: string;
  draftType?: DraftEmailType;
  modifiedQuotedContent?: string | null;
}

export interface DraftWorthinessOptions {
  countsRecipients: boolean;
  countsSubject: boolean;
}

const hasAnyRecipient = (draftData: DraftData): boolean =>
  draftData.toAddresses.length > 0 ||
  draftData.ccAddresses.length > 0 ||
  draftData.bccAddresses.length > 0;

const ADDRESS_SIGNATURE_SEPARATOR = ',';

// A count-based signature treats "swap one recipient for another" as no
// change, so the save gets deduped and the server never learns about it.
const buildAddressListSignature = (addresses: Address[]): string =>
  addresses
    .map((address) => address.value.trim().toLowerCase())
    .sort()
    .join(ADDRESS_SIGNATURE_SEPARATOR);

export const generateDraftSaveId = (draftData: DraftData): string =>
  JSON.stringify({
    s: draftData.subject || '',
    m: normalizeDraftHtmlForComparison(draftData.message || ''),
    to: buildAddressListSignature(draftData.toAddresses),
    cc: buildAddressListSignature(draftData.ccAddresses),
    bcc: buildAddressListSignature(draftData.bccAddresses),
    a: draftData.attachments?.length || 0,
    mid: draftData.messageId,
    mqc: normalizeDraftHtmlForComparison(draftData.modifiedQuotedContent || ''),
  });

export const hasDraftWorthyContent = (
  draftData: DraftData,
  { countsRecipients, countsSubject }: DraftWorthinessOptions,
): boolean =>
  validateEditorContent(
    draftData.message ?? '',
    draftData.modifiedQuotedContent ?? null,
    true,
  ) ||
  Boolean(draftData.modifiedQuotedContent) ||
  (countsSubject && Boolean(draftData.subject?.trim())) ||
  Boolean(draftData.attachments?.length) ||
  (countsRecipients && hasAnyRecipient(draftData));

export { isThreadBoundHandler };

export interface DraftConflictState {
  serverDraftVersion?: string;
  isDeletedOnProvider?: boolean;
}

export interface DetachedDraft {
  draftId: string;
  pinnedAccount?: DraftAccount;
  draftVersion?: string;
  hasDraftVersionConflict: boolean;
  serverDraftVersion?: string;
  isDeletedOnProvider?: boolean;
  lastSaveRequest: string;
}

export interface DraftSavedCallbackData {
  draftId: string;
  subject?: string;
  message?: string;
  toAddresses: Address[];
  ccAddresses: Address[];
  bccAddresses: Address[];
  createdAt?: number;
}

interface UseSmartDraftManagerProps {
  initialData: DraftData;
  draftId?: string;
  selectedHandlerButton?: HandlerButton;
  userCommunication?: UserCommunication;
  threadContext?: UserCommunication;
  inboxEntryId?: string;
  onDraftCreated?: (newDraftId: string) => void;
  onSendSuccess?: () => void;
  onSaveCommitted?: (
    saveId: string,
    draftId: string,
    draftVersion?: string,
  ) => void;
  onDraftVersionLoaded?: (draftVersion: string) => void;
  getLatestDraftData?: () => DraftData;
  isAutoRetryDisabled?: boolean;
}

const AUTO_SAVE_TIMINGS = 5000 as const;

export enum SaveTriggerType {
  TEXT = 'text',
  SUBJECT = 'subject',
  RECIPIENTS = 'recipients',
  PASTE = 'paste',
  BULK = 'bulk',
  BLUR = 'blur',
}
interface ChangeContext {
  type: SaveTriggerType;
  previousContent?: string;
  isUserInitiated: boolean;
}

export enum SaveStatus {
  IDLE = 'idle',
  SAVING = 'saving',
  SAVED = 'saved',
  ERROR = 'error',
  DELETING = 'deleting',
  CONFLICT = 'conflict',
}

const TAB_URL_PARAM = 'tab';

const ERROR_NAMES = {
  ABORT: 'AbortError',
} as const;

const PATH_SEGMENTS = {
  PREVIEW: '/preview/',
} as const;

const LOG_MESSAGES = {
  CLEARED_PENDING_SAVE: 'Cleared pending save timer',
  SKIP_SENDING: 'Skipping save: message sending or already sent',
  SKIP_DUPLICATE: 'Skipping duplicate save request',
  START_SAVE: 'Starting new save request',
  DISCARD_OUTDATED: 'Discarding outdated save response',
  SAVE_ABORTED: 'Save request was aborted',
  IGNORE_ERROR_OUTDATED: 'Ignoring error from outdated save request',
  IGNORE_ERROR_DISCARDED: 'Ignoring error from a sent/discarded draft',
  SAVE_CANCELLED: 'Save cancelled: message is being sent',
  SKIP_INSIGNIFICANT: 'Skipping insignificant text change',
  DRAFT_ID_RESET: 'Draft ID reset, new draft will be created on next save',
  SKIP_EMPTY_DRAFT: 'Skipping save: no draft-worthy content yet',
  SKIP_VERSION_CONFLICT: 'Skipping save: draft version conflict unresolved',
  SAVE_VERSION_CONFLICT: 'Draft version conflict on save, autosave stopped',
  SAVE_DRAFT_DELETED: 'Draft deleted on the provider, autosave stopped',
  SEND_VERSION_CONFLICT: 'Draft version conflict on send, send aborted',
  SEND_ACCOUNT_MISMATCH: 'Draft lives in a different account than From',
  RETRY_SCHEDULED: 'Scheduled automatic save retry',
  RETRY_FIRING: 'Retrying failed save',
  SAVE_PAYLOAD_TOO_LARGE: 'Draft too large to save, auto-retry skipped',
  SEND_PAYLOAD_TOO_LARGE: 'Draft too large to send, send aborted',
} as const;

const ERROR_MESSAGES = {
  NO_SAVE_DATA: 'No data returned from save draft API',
  DRAFT_ID_REQUIRED: 'Draft ID is required to send draft',
  SAVE_FAILED: 'Save failed',
  FAILED_TO_SAVE: 'Failed to save draft:',
  FAILED_TO_SEND: 'Failed to send draft:',
  PAYLOAD_TOO_LARGE: 'Attachments exceed the request size limit',
} as const;

export enum SaveFailureReason {
  PAYLOAD_TOO_LARGE = 'payload-too-large',
}

export interface SaveState {
  status: SaveStatus;
  lastSavedAt?: Date;
  error?: string;
  retrying?: boolean;
  reason?: SaveFailureReason;
}

export interface SaveDraftOptions {
  errorToast?: SaveErrorToast;
}

// Background saves stay silent on a generic failure (the header indicator says it);
// only failures that need a user action (conflict, payload too large) toast.
const AUTOSAVE_OPTIONS: SaveDraftOptions = {
  errorToast: SaveErrorToast.ACTIONABLE_ONLY,
};
const RETRY_OPTIONS: SaveDraftOptions = AUTOSAVE_OPTIONS;

const RETRY_TRIGGER_EVENTS = {
  ONLINE: 'online',
  VISIBILITY_CHANGE: 'visibilitychange',
} as const;

export function useSmartDraftManager({
  initialData,
  draftId,
  selectedHandlerButton,
  userCommunication,
  threadContext,
  inboxEntryId,
  onDraftCreated,
  onSendSuccess,
  onSaveCommitted,
  onDraftVersionLoaded,
  getLatestDraftData,
  isAutoRetryDisabled = false,
}: Readonly<UseSmartDraftManagerProps>) {
  const onDraftCreatedRef = useRef(onDraftCreated);
  onDraftCreatedRef.current = onDraftCreated;

  const onSaveCommittedRef = useRef(onSaveCommitted);
  onSaveCommittedRef.current = onSaveCommitted;

  const onDraftVersionLoadedRef = useRef(onDraftVersionLoaded);
  onDraftVersionLoadedRef.current = onDraftVersionLoaded;

  const onSendSuccessRef = useRef(onSendSuccess);
  onSendSuccessRef.current = onSendSuccess;

  const getLatestDraftDataRef = useRef(getLatestDraftData);
  getLatestDraftDataRef.current = getLatestDraftData;

  const isAutoRetryDisabledRef = useRef(isAutoRetryDisabled);
  isAutoRetryDisabledRef.current = isAutoRetryDisabled;

  const { t } = useTranslation();
  const { errorMessage, successMessage } = useCustomToast();

  const removedAttachmentNamesRef = useRef(new Set<string>());
  const recordRemovedAttachment = useCallback((name: string) => {
    removedAttachmentNamesRef.current.add(name);
  }, []);
  // The server deletes a draft attachment only by name, so only names the user deleted are sent.
  const getAttachmentsToRemove = (attachments?: FileAttachment[]): string[] => {
    const present = new Set(attachments?.map(({ name }) => name));
    return [...removedAttachmentNamesRef.current].filter(
      (name) => !present.has(name),
    );
  };
  const openedDraftIdRef = useRef(draftId);
  useEffect(() => {
    const previous = openedDraftIdRef.current;
    openedDraftIdRef.current = draftId;
    if (previous && draftId && previous !== draftId) {
      removedAttachmentNamesRef.current.clear();
    }
  }, [draftId]);

  const { saveDraft: saveDraftAPI } = useSaveDraft();
  const { sendDraft: sendDraftAPI } = useSendDraft();
  const { deleteDraft: deleteDraftFromStore, upsertDraft } =
    useUserCommunicationsStore();
  const { upsertDraftInInboxEntry } = useInboxEntriesStore(
    (state) => state.taskManager.activeTaskGroup,
  );
  const { currentIntegrationAccount, integrationAccounts } =
    useIntegrationsStore();
  const { labelIds } = userCommunication ?? {};
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSendingDraft, setIsSendingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState<string | undefined>(
    draftId,
  );
  const [saveState, setSaveState] = useState<SaveState>({
    status: SaveStatus.IDLE,
  });
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get(TAB_URL_PARAM);

  const saveDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  // Never armed together with saveDebounceTimer: an edit's debounce supersedes it.
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const retryAttemptRef = useRef<number>(0);
  const runRetryRef = useRef<() => void>(() => undefined);
  const pendingSaveDataRef = useRef<DraftData | null>(null);
  const pendingRetryFallbackRef = useRef<DraftData | null>(null);
  // Bumped on every real edit funnelled through autoSaveOnChange, and
  // snapshotted alongside the draft data a save is scheduled for. A save only
  // gets to claim "clean"/SAVED once its snapshotted revision still matches
  // the live counter — otherwise an edit landed while it was in flight and
  // the state must stay dirty/pending. See performSaveDraft.
  const editRevisionRef = useRef<number>(0);
  const pendingSaveRevisionRef = useRef<number>(0);

  const previousContent = useRef<DraftData>(initialData);
  const lastSaveRequest = useRef<string>('');

  const lastSaveTimestampRef = useRef<number>(0);
  const pendingSaveTimestampRef = useRef<number>(0);
  const shouldSkipSaveRef = useRef<boolean>(false);
  const isUnmountedRef = useRef<boolean>(false);
  const inFlightSaveRef = useRef<Promise<boolean> | null>(null);
  const saveHoldRef = useRef<Promise<unknown>>(Promise.resolve());
  const isSendingRef = useRef<boolean>(false);
  const hasUserEditedRecipientsRef = useRef<boolean>(false);
  const hasUserEditedSubjectRef = useRef<boolean>(false);
  const currentDraftIdRef = useRef<string | undefined>(currentDraftId);
  // The account the create request actually targeted. Update, send and delete
  // follow it, never the live From, which can change after the draft exists.
  const pinnedAccountRef = useRef<DraftAccount | undefined>(undefined);
  // The provider's opaque draft version, echoed back as expectedDraftVersion on the next
  // write to this same draft (save or send) so the server can detect a change made
  // elsewhere. A distinct concept from editRevisionRef: that one tracks local edits to
  // decide when this hook's own state is "clean"; this one tracks the server's copy.
  const draftVersionRef = useRef<string | undefined>(undefined);
  // Lets a create still in flight in another compose of this lineage queue this compose's writes behind it.
  const writeLineageRef = useRef<string | undefined>(undefined);
  // Set once a write comes back as DRAFT_VERSION_CONFLICT. Blocks further autosave
  // attempts for this draft instance so a stale expectedDraftVersion never gets retried
  // silently over whatever changed the draft elsewhere.
  const hasDraftVersionConflictRef = useRef<boolean>(false);
  const [draftConflict, setDraftConflict] = useState<DraftConflictState | null>(
    null,
  );
  const draftConflictRef = useRef(draftConflict);
  draftConflictRef.current = draftConflict;
  // Survives unmount, when the state above is never set but a background save can still hit a conflict.
  const latchedConflictRef = useRef<DraftConflictState | null>(null);
  // True once a background save toasted payload-too-large; re-armed by the next successful save.
  const hasToastedPayloadTooLargeRef = useRef<boolean>(false);

  const isReplyHandler =
    selectedHandlerButton === HandlerButton.REPLY ||
    selectedHandlerButton === HandlerButton.REPLY_ALL;

  const hasInheritedSubject = Boolean(userCommunication?.subject?.trim());

  const getDraftWorthinessOptions = useCallback(
    (): DraftWorthinessOptions => ({
      countsRecipients: !isReplyHandler || hasUserEditedRecipientsRef.current,
      countsSubject: !hasInheritedSubject || hasUserEditedSubjectRef.current,
    }),
    [isReplyHandler, hasInheritedSubject],
  );

  const isDraftWorthy = useCallback(
    (draftData: DraftData): boolean =>
      Boolean(currentDraftIdRef.current) ||
      hasDraftWorthyContent(draftData, getDraftWorthinessOptions()),
    [getDraftWorthinessOptions],
  );

  const seedDraftVersionIfAbsent = useCallback((version: string) => {
    draftVersionRef.current ??= version;
    onDraftVersionLoadedRef.current?.(version);
  }, []);

  const mergedLabelIds = [
    ...(labelIds ?? []),
    ...(threadContext?.labelIds ?? []),
  ];
  const isInboxEmail = mergedLabelIds.includes(LabelId.INBOX);
  const isSentEmail = mergedLabelIds.includes(LabelId.SENT);

  const navigate = useNavigate();

  const [hasStartedComposing, setHasStartedComposing] = useState(
    Boolean(draftId),
  );

  const [isDirty, setIsDirty] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const initialContentRef = useRef<DraftData>(initialData);

  useEffect(() => {
    currentDraftIdRef.current = currentDraftId;
  }, [currentDraftId]);

  // An opened draft is pinned to the connected account its row says it lives
  // in; the account's own platform is used because the row's platform value
  // uses a different vocabulary than the integration accounts.
  const openedAccountEmail = userCommunication?.userEmailAddress;
  useEffect(() => {
    if (
      !draftId ||
      !openedAccountEmail ||
      !currentIntegrationAccount ||
      pinnedAccountRef.current ||
      currentDraftIdRef.current !== draftId
    ) {
      return;
    }
    pinnedAccountRef.current =
      resolveDraftAccount(
        openedAccountEmail,
        currentIntegrationAccount,
        integrationAccounts ?? [],
        { strict: true },
      ) ?? undefined;
  }, [
    draftId,
    openedAccountEmail,
    currentIntegrationAccount,
    integrationAccounts,
  ]);

  useEffect(() => {
    if (!isInitialLoadComplete) {
      initialContentRef.current = { ...initialData };
      setIsInitialLoadComplete(true);
      setIsDirty(false);
    }
  }, [initialData, isInitialLoadComplete]);

  const previousSourceMessageIdRef = useRef(userCommunication?.messageId);
  useEffect(() => {
    // Reset only on an actual source-message change, never on unmount — an
    // unmount cleanup here would clear a discard set right before it and
    // let an in-flight save resurrect the row.
    const messageId = userCommunication?.messageId;
    if (previousSourceMessageIdRef.current === messageId) return;
    previousSourceMessageIdRef.current = messageId;
    shouldSkipSaveRef.current = false;
  }, [userCommunication?.messageId]);

  // initialData.message hydrates after mount, so until the first edit or save
  // the baseline must track it or a first clear-everything edit compares
  // against '' and is skipped as insignificant.
  useEffect(() => {
    if (isDirty || lastSaveTimestampRef.current !== 0) return;
    previousContent.current = {
      ...previousContent.current,
      message: initialData.message,
    };
  }, [initialData.message, isDirty]);

  const markDirty = useCallback(() => {
    setHasStartedComposing(true);
    setIsDirty(true);
  }, []);

  const markClean = useCallback(() => setIsDirty(false), []);

  const markContentSaved = useCallback((saveId: string) => {
    lastSaveRequest.current = saveId;
  }, []);

  const clearDebounce = useCallback(() => {
    if (saveDebounceTimer.current) {
      clearTimeout(saveDebounceTimer.current);
      saveDebounceTimer.current = null;
      pendingSaveDataRef.current = null;
      log.debug(LOG_MESSAGES.CLEARED_PENDING_SAVE);
    }
  }, []);

  const clearRetryTimer = useCallback((): boolean => {
    if (!retryTimerRef.current) return false;
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
    return true;
  }, []);

  // Send, close, discard and conflict all stop the retry through here.
  const cancelPendingSave = useCallback(() => {
    clearDebounce();
    if (clearRetryTimer() && !isUnmountedRef.current) {
      setSaveState((previous) =>
        previous.retrying ? { ...previous, retrying: false } : previous,
      );
    }
  }, [clearDebounce, clearRetryTimer]);

  // shouldSkipSaveRef means "sent or discarded": once set, no further save
  // commit for this draft instance should ever reach the store.
  const markDraftDiscarded = useCallback(() => {
    shouldSkipSaveRef.current = true;
    cancelPendingSave();
  }, [cancelPendingSave]);

  const enterDraftConflict = useCallback(
    (serverDraftVersion?: string) => {
      hasDraftVersionConflictRef.current = true;
      latchedConflictRef.current = { serverDraftVersion };
      cancelPendingSave();
      if (!isUnmountedRef.current) {
        setDraftConflict({ serverDraftVersion });
        setSaveState({ status: SaveStatus.CONFLICT });
      }
    },
    [cancelPendingSave],
  );

  // Same autosave pause as a conflict: a save of a deleted draft would only fail again.
  const enterDraftDeleted = useCallback(() => {
    hasDraftVersionConflictRef.current = true;
    latchedConflictRef.current = { isDeletedOnProvider: true };
    cancelPendingSave();
    if (!isUnmountedRef.current) {
      setDraftConflict({ isDeletedOnProvider: true });
      setSaveState({ status: SaveStatus.CONFLICT });
    }
  }, [cancelPendingSave]);

  const { awaitVersionProbe } = useLoadedDraftVersion({
    draftId,
    userCommunication,
    draftType: handleSelectDraftReqType(selectedHandlerButton),
    seedDraftVersionIfAbsent,
    onDraftNotFound: enterDraftDeleted,
  });

  const resolveDraftConflict = useCallback((): string | undefined => {
    const { serverDraftVersion } = draftConflictRef.current ?? {};
    hasDraftVersionConflictRef.current = false;
    latchedConflictRef.current = null;
    setDraftConflict(null);
    setSaveState({ status: SaveStatus.IDLE });
    return serverDraftVersion;
  }, []);

  const clearAllTimers = useCallback(() => {
    if (saveDebounceTimer.current) clearTimeout(saveDebounceTimer.current);
    clearRetryTimer();
  }, [clearRetryTimer]);

  const isSignificantChange = useCallback(
    (oldValue: string = '', newValue: string = ''): boolean => {
      const normalizedOld = normalizeDraftHtmlForComparison(oldValue);
      const normalizedNew = normalizeDraftHtmlForComparison(newValue);

      if (!normalizedOld && normalizedNew) return true;

      return normalizedOld !== normalizedNew;
    },
    [],
  );

  const shouldToastPayloadTooLarge = (errorToast: SaveErrorToast): boolean => {
    if (errorToast === SaveErrorToast.NONE) return false;
    if (errorToast === SaveErrorToast.ALWAYS) return true;
    if (hasToastedPayloadTooLargeRef.current) return false;
    hasToastedPayloadTooLargeRef.current = true;
    return true;
  };

  const canAutoRetry = (): boolean =>
    !isUnmountedRef.current &&
    !shouldSkipSaveRef.current &&
    !isSendingRef.current &&
    !hasDraftVersionConflictRef.current &&
    !isAutoRetryDisabledRef.current;

  // Returns whether a retry is now scheduled, for the indicator.
  const scheduleAutoRetry = (failedData: DraftData): boolean => {
    if (!canAutoRetry() || saveDebounceTimer.current) return false;
    clearRetryTimer();
    const delays = DRAFT_AUTO_RETRY_DELAYS_MS;
    const delay = delays[Math.min(retryAttemptRef.current, delays.length - 1)];
    retryAttemptRef.current += 1;
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      runRetryRef.current();
    }, delay);
    log.debug(LOG_MESSAGES.RETRY_SCHEDULED, { delay });
    pendingRetryFallbackRef.current = failedData;
    return true;
  };

  // A request over the size limit can never succeed as-is, so it never arms the retry.
  const handleSavePayloadTooLarge = (errorToast: SaveErrorToast) => {
    log.warn(LOG_MESSAGES.SAVE_PAYLOAD_TOO_LARGE);
    if (shouldToastPayloadTooLarge(errorToast) && !isUnmountedRef.current) {
      errorMessage(
        t('inbox.userCommunication.actions.savePayloadTooLargeError'),
      );
    }
    lastSaveRequest.current = '';
    clearRetryTimer();
    if (!isUnmountedRef.current) {
      setSaveState({
        status: SaveStatus.ERROR,
        error: ERROR_MESSAGES.PAYLOAD_TOO_LARGE,
        reason: SaveFailureReason.PAYLOAD_TOO_LARGE,
      });
    }
  };

  const runAutoRetry = () => {
    if (!canAutoRetry()) return;
    log.debug(LOG_MESSAGES.RETRY_FIRING);
    const latest =
      getLatestDraftDataRef.current?.() ?? pendingRetryFallbackRef.current;
    if (!latest) return;
    void saveDraft(latest, editRevisionRef.current, RETRY_OPTIONS);
  };
  runRetryRef.current = runAutoRetry;

  const retryNow = () => {
    if (clearRetryTimer()) runAutoRetry();
  };

  const resetAutoRetry = () => {
    retryAttemptRef.current = 0;
    clearRetryTimer();
  };

  const performSaveDraft = async (
    draftData: DraftData,
    revision: number = editRevisionRef.current,
    { errorToast = SaveErrorToast.ALWAYS }: SaveDraftOptions = {},
  ): Promise<boolean> => {
    if (shouldSkipSaveRef.current || isSendingRef.current) {
      log.debug(LOG_MESSAGES.SKIP_SENDING);
      return true;
    }

    if (hasDraftVersionConflictRef.current) {
      log.debug(LOG_MESSAGES.SKIP_VERSION_CONFLICT);
      return true;
    }

    if (!isDraftWorthy(draftData)) {
      log.debug(LOG_MESSAGES.SKIP_EMPTY_DRAFT);
      return true;
    }

    const saveId = generateDraftSaveId(draftData);
    const isCreateRequest = !currentDraftIdRef.current;

    if (lastSaveRequest.current === saveId) {
      log.debug(LOG_MESSAGES.SKIP_DUPLICATE);
      return true;
    }

    const currentTimestamp = Date.now();
    pendingSaveTimestampRef.current = currentTimestamp;

    log.debug(LOG_MESSAGES.START_SAVE, { timestamp: currentTimestamp });

    try {
      if (!isUnmountedRef.current) {
        setIsSavingDraft(true);
        setSaveState({ status: SaveStatus.SAVING });
      }
      lastSaveRequest.current = saveId;

      const allRecipients = [
        ...draftData.toAddresses.map((address) => ({
          ...address,
          type: RecipientType.TO,
        })),
        ...draftData.ccAddresses.map((address) => ({
          ...address,
          type: RecipientType.CC,
        })),
        ...draftData.bccAddresses.map((address) => ({
          ...address,
          type: RecipientType.BCC,
        })),
      ];

      const savedDraftData = await saveDraftAPI(
        draftData.message,
        draftData.subject,
        allRecipients,
        draftData.attachments,
        currentDraftIdRef.current,
        draftData.fromEmail,
        draftData.messageId,
        handleSelectDraftReqType(selectedHandlerButton),
        draftData.modifiedQuotedContent ?? undefined,
        draftVersionRef.current,
        currentDraftIdRef.current ? pinnedAccountRef.current : undefined,
        getAttachmentsToRemove(draftData.attachments),
        writeLineageRef.current,
      );

      if (isDraftConflictResult(savedDraftData)) {
        log.warn(LOG_MESSAGES.SAVE_VERSION_CONFLICT, {
          currentDraftVersion: savedDraftData.currentDraftVersion,
        });
        enterDraftConflict(savedDraftData.currentDraftVersion);
        lastSaveRequest.current = '';
        return false;
      }

      if (isPayloadTooLargeResult(savedDraftData)) {
        handleSavePayloadTooLarge(errorToast);
        return false;
      }

      if (!savedDraftData?.messageId) {
        throw new Error(ERROR_MESSAGES.NO_SAVE_DATA);
      }

      const {
        messageId: apiDraftId,
        messageRawText,
        platform,
        draftVersion,
        draftAccount,
      } = savedDraftData;
      draftVersionRef.current = draftVersion;

      if (currentTimestamp < lastSaveTimestampRef.current) {
        log.warn(LOG_MESSAGES.DISCARD_OUTDATED, {
          currentTimestamp,
          lastSaveTimestamp: lastSaveTimestampRef.current,
        });
        return true;
      }

      if (shouldSkipSaveRef.current) {
        log.debug(LOG_MESSAGES.SKIP_SENDING);
        if (!currentDraftIdRef.current) {
          currentDraftIdRef.current = apiDraftId;
          if (isCreateRequest) pinnedAccountRef.current = draftAccount;
        }
        return true;
      }

      if (apiDraftId) {
        lastSaveTimestampRef.current = currentTimestamp;

        if (!currentDraftIdRef.current) {
          currentDraftIdRef.current = apiDraftId;
          if (isCreateRequest) pinnedAccountRef.current = draftAccount;
          if (!isUnmountedRef.current) {
            setCurrentDraftId(apiDraftId);
          }
          // A create landing after close would set the id on whatever dialog opened next.
          if (!isUnmountedRef.current) {
            onDraftCreatedRef.current?.(apiDraftId);
          }
        }

        commitSavedDraftToStores({
          draftId: currentDraftIdRef.current ?? apiDraftId,
          platform,
          messageRawText,
          draftVersion,
          pinnedAccountId: pinnedAccountRef.current?.accountId,
          currentAccountDisplayName: currentIntegrationAccount?.displayName,
          draftData,
          recipients: allRecipients,
          handler: selectedHandlerButton,
          sourceUserCommunication: userCommunication,
          threadContext,
          isInboxEmail,
          isSentEmail,
          tabParam: tabParam ?? undefined,
          inboxEntryId,
          actions: { upsertDraft, upsertDraftInInboxEntry },
        });

        if (!hasStartedComposing && !isUnmountedRef.current) {
          setHasStartedComposing(true);
        }

        // A newer edit landed while this save was in flight (revision moved
        // on) — this save's confirmation is already stale, so leave
        // isDirty/saveState alone rather than falsely reporting "Saved".
        const confirmsCurrentRevision = revision === editRevisionRef.current;
        hasToastedPayloadTooLargeRef.current = false;
        resetAutoRetry();

        if (!isUnmountedRef.current && confirmsCurrentRevision) {
          setSaveState({
            status: SaveStatus.SAVED,
            lastSavedAt: new Date(),
          });
          setIsDirty(false);
        }

        previousContent.current = { ...draftData };
        onSaveCommittedRef.current?.(
          saveId,
          currentDraftIdRef.current ?? apiDraftId,
          draftVersion,
        );
        return true;
      }
    } catch (error) {
      if (error instanceof Error && error.name === ERROR_NAMES.ABORT) {
        log.debug(LOG_MESSAGES.SAVE_ABORTED);
        return true;
      }

      if (currentTimestamp < lastSaveTimestampRef.current) {
        log.warn(LOG_MESSAGES.IGNORE_ERROR_OUTDATED);
        return true;
      }

      if (shouldSkipSaveRef.current) {
        log.debug(LOG_MESSAGES.IGNORE_ERROR_DISCARDED);
        lastSaveRequest.current = '';
        return true;
      }

      if (!isCreateRequest && isDraftNotFoundError(error)) {
        log.warn(LOG_MESSAGES.SAVE_DRAFT_DELETED);
        enterDraftDeleted();
        lastSaveRequest.current = '';
        return false;
      }

      log.error(ERROR_MESSAGES.FAILED_TO_SAVE, error);
      if (errorToast === SaveErrorToast.ALWAYS) {
        errorMessage(t('inbox.userCommunication.actions.saveDraftError'));
      }
      lastSaveRequest.current = '';
      const retrying = scheduleAutoRetry(draftData);
      if (!isUnmountedRef.current) {
        setSaveState({
          status: SaveStatus.ERROR,
          error:
            error instanceof Error ? error.message : ERROR_MESSAGES.SAVE_FAILED,
          retrying,
        });
      }
      return false;
    } finally {
      if (!isUnmountedRef.current) {
        setIsSavingDraft(false);
      }
    }

    return true;
  };

  // A hold set while a save waited on an earlier one still gates that save.
  const awaitSaveHold = async (): Promise<void> => {
    let hold: Promise<unknown>;
    do {
      hold = saveHoldRef.current;
      await hold;
    } while (hold !== saveHoldRef.current);
  };

  const saveDraft = (
    draftData: DraftData,
    revision: number = editRevisionRef.current,
    options?: SaveDraftOptions,
  ): Promise<boolean> => {
    const previousSave = inFlightSaveRef.current ?? Promise.resolve();
    const promise = previousSave
      .then(awaitVersionProbe)
      .then(awaitSaveHold)
      .then(() => performSaveDraft(draftData, revision, options))
      .finally(() => {
        if (inFlightSaveRef.current === promise) {
          inFlightSaveRef.current = null;
        }
      });
    inFlightSaveRef.current = promise;
    return promise;
  };

  // Saves queue behind `pending` so a create still in flight elsewhere is never duplicated.
  const holdSavesUntil = useCallback((pending: Promise<unknown>) => {
    saveHoldRef.current = pending;
    if (!isUnmountedRef.current) {
      setSaveState({ status: SaveStatus.SAVING });
    }
    void pending.finally(() => {
      if (!isUnmountedRef.current) {
        setSaveState((current) =>
          current.status === SaveStatus.SAVING
            ? { status: SaveStatus.IDLE }
            : current,
        );
      }
    });
  }, []);

  const waitForInFlightSave = useCallback(
    (): Promise<boolean | undefined> =>
      inFlightSaveRef.current ?? Promise.resolve(undefined),
    [],
  );

  const getCurrentDraftId = useCallback(
    (): string | undefined => currentDraftIdRef.current,
    [],
  );

  const getLatchedConflict = useCallback(
    (): DraftConflictState | null =>
      hasDraftVersionConflictRef.current ? latchedConflictRef.current : null,
    [],
  );

  const hasVersionConflict = useCallback(
    (): boolean => hasDraftVersionConflictRef.current,
    [],
  );

  const hasScheduledSave = useCallback(
    (): boolean => saveDebounceTimer.current !== null,
    [],
  );

  const hasPendingSave = useCallback(
    (): boolean =>
      saveDebounceTimer.current !== null || inFlightSaveRef.current !== null,
    [],
  );

  const debouncedSave = useCallback(
    (draftData: DraftData, context: ChangeContext) => {
      const { type } = context;

      clearDebounce();
      clearRetryTimer();

      // Captured at schedule time, not when the timer fires — an
      // already-fired-but-still-queued save (waiting behind an in-flight
      // one) must keep the revision that matches the data it actually
      // carries, not whatever the counter has moved on to by then.
      const scheduledRevision = editRevisionRef.current;
      pendingSaveDataRef.current = { ...draftData };
      pendingSaveRevisionRef.current = scheduledRevision;
      saveDebounceTimer.current = setTimeout(() => {
        // Cleared here, not only by cancelPendingSave/flushPendingSave — a
        // natural fire otherwise leaves a stale (already-consumed) timer id
        // in the ref, which would make hasPendingSave() lie to
        // handleCloseWithDraftSaving's close guard.
        saveDebounceTimer.current = null;
        pendingSaveDataRef.current = null;
        if (shouldSkipSaveRef.current || isSendingRef.current) {
          log.debug(LOG_MESSAGES.SAVE_CANCELLED);
          return;
        }
        if (hasDraftVersionConflictRef.current) {
          log.debug(LOG_MESSAGES.SKIP_VERSION_CONFLICT);
          return;
        }
        saveDraft({ ...draftData }, scheduledRevision, AUTOSAVE_OPTIONS);
      }, AUTO_SAVE_TIMINGS);

      log.debug(
        `Scheduled save with ${AUTO_SAVE_TIMINGS}ms delay for type: ${type}`,
      );
    },
    [saveDraft, clearDebounce, clearRetryTimer],
  );

  // Flushes a pending debounce (unmount, tab hidden) instead of dropping it;
  // performSaveDraft still applies its own shouldSkipSaveRef/isSendingRef
  // checks, so a send or discard right before the flush still wins.
  const flushPendingSave = useCallback(
    (override?: DraftData): Promise<boolean> | undefined => {
      if (!saveDebounceTimer.current) {
        return undefined;
      }
      clearTimeout(saveDebounceTimer.current);
      saveDebounceTimer.current = null;
      const pendingData = override ?? pendingSaveDataRef.current;
      // An override carries fresher props than what the debounce was
      // scheduled with, so it must be judged against the live revision, not
      // the one the (now-superseded) scheduled data snapshotted.
      const revision = override
        ? editRevisionRef.current
        : pendingSaveRevisionRef.current;
      pendingSaveDataRef.current = null;
      return pendingData
        ? saveDraft(pendingData, revision, AUTOSAVE_OPTIONS)
        : undefined;
    },
    [saveDraft],
  );

  const autoSaveOnChange = useCallback(
    (
      draftData: DraftData,
      changeType: SaveTriggerType = SaveTriggerType.TEXT,
    ) => {
      if (!isInitialLoadComplete) {
        return;
      }

      if (!isDraftWorthy(draftData)) {
        cancelPendingSave();
        if (isDirty) setIsDirty(false);
        return;
      }

      const previousMessage = previousContent.current.message;
      const hasPersistedBaseline = Boolean(currentDraftIdRef.current);

      if (
        changeType === SaveTriggerType.TEXT &&
        hasPersistedBaseline &&
        !isSignificantChange(previousMessage, draftData.message)
      ) {
        log.debug(LOG_MESSAGES.SKIP_INSIGNIFICANT);
        return;
      }

      if (!hasStartedComposing) {
        setHasStartedComposing(true);
      }

      if (!isDirty) {
        setIsDirty(true);
      }

      editRevisionRef.current += 1;

      const context: ChangeContext = {
        type: changeType,
        previousContent:
          changeType === SaveTriggerType.TEXT ? previousMessage : undefined,
        isUserInitiated: true,
      };

      debouncedSave(draftData, context);
    },
    [
      debouncedSave,
      hasStartedComposing,
      isDirty,
      isInitialLoadComplete,
      isDraftWorthy,
      isSignificantChange,
      cancelPendingSave,
    ],
  );

  const autoSaveText = useCallback(
    (draftData: DraftData) => autoSaveOnChange(draftData, SaveTriggerType.TEXT),
    [autoSaveOnChange],
  );

  const autoSaveSubject = useCallback(
    (draftData: DraftData) => {
      hasUserEditedSubjectRef.current = true;
      autoSaveOnChange(draftData, SaveTriggerType.SUBJECT);
    },
    [autoSaveOnChange],
  );

  const autoSaveRecipients = useCallback(
    (draftData: DraftData) => {
      hasUserEditedRecipientsRef.current = true;
      autoSaveOnChange(draftData, SaveTriggerType.RECIPIENTS);
    },
    [autoSaveOnChange],
  );

  const autoSavePaste = useCallback(
    (draftData: DraftData) =>
      autoSaveOnChange(draftData, SaveTriggerType.PASTE),
    [autoSaveOnChange],
  );

  const getPinnedAccount = useCallback(
    (): DraftAccount | undefined => pinnedAccountRef.current,
    [],
  );

  const resolveSelectedAccount = useCallback(
    (fromEmail: string | undefined): DraftAccount | undefined =>
      currentIntegrationAccount
        ? (resolveDraftAccount(
            fromEmail,
            currentIntegrationAccount,
            integrationAccounts ?? [],
            { strict: true },
          ) ?? undefined)
        : undefined,
    [currentIntegrationAccount, integrationAccounts],
  );

  const isDraftInOtherAccount = (fromEmail: string | undefined): boolean => {
    const pinned = pinnedAccountRef.current;
    const selected = resolveSelectedAccount(fromEmail);
    return (
      !isThreadBoundHandler(selectedHandlerButton) &&
      Boolean(pinned && selected) &&
      !isSameDraftAccount(pinned, selected)
    );
  };

  const sendDraftEmail = async (draftData: DraftData): Promise<boolean> => {
    try {
      if (!isUnmountedRef.current) {
        setIsSendingDraft(true);
      }
      if (hasDraftVersionConflictRef.current) {
        log.debug(LOG_MESSAGES.SKIP_VERSION_CONFLICT);
        return false;
      }
      if (!currentDraftIdRef.current) {
        log.error(ERROR_MESSAGES.DRAFT_ID_REQUIRED);
        errorMessage(
          t('inbox.userCommunication.actions.sendDraftNotReadyError'),
        );
        return false;
      }

      if (
        !draftData.toAddresses?.length ||
        !draftData.subject?.trim() ||
        !draftData.message?.trim()
      ) {
        errorMessage(
          t('inbox.userCommunication.actions.sendDraftValidationError'),
        );
        return false;
      }

      if (!resolveSelectedAccount(draftData.fromEmail)) {
        errorMessage(
          t('inbox.userCommunication.actions.sendDraftNotReadyError'),
        );
        return false;
      }

      if (isDraftInOtherAccount(draftData.fromEmail)) {
        log.error(LOG_MESSAGES.SEND_ACCOUNT_MISMATCH, {
          draftId: currentDraftIdRef.current,
        });
        errorMessage(
          t('inbox.userCommunication.actions.draftNotFoundInAccountError'),
        );
        return false;
      }

      const taggedToAddresses = draftData.toAddresses.map((address) => ({
        ...address,
        type: RecipientType.TO,
      }));
      const taggedCcAddresses = draftData.ccAddresses.map((address) => ({
        ...address,
        type: RecipientType.CC,
      }));
      const taggedBccAddresses = draftData.bccAddresses.map((address) => ({
        ...address,
        type: RecipientType.BCC,
      }));

      const sendDraftProperties: SendDraftProperties = {
        draftId: currentDraftIdRef.current,
        toAddresses: taggedToAddresses,
        ccAddresses:
          taggedCcAddresses.length > 0 ? taggedCcAddresses : undefined,
        bccAddresses:
          taggedBccAddresses.length > 0 ? taggedBccAddresses : undefined,
        body: draftData.message,
        subject: draftData.subject,
        attachments:
          draftData.attachments?.length !== 0
            ? draftData.attachments
            : undefined,
        fromEmail: draftData.fromEmail,
        draftType: draftData.draftType,
        modifiedQuotedContent: draftData.modifiedQuotedContent ?? undefined,
        expectedDraftVersion: draftVersionRef.current,
        pinnedAccount: pinnedAccountRef.current,
        attachmentsToRemove: [
          ...new Set([
            ...getAttachmentsToRemove(draftData.attachments),
            ...(draftData.attachmentsToRemove ?? []),
          ]),
        ],
        lineageId: writeLineageRef.current,
      };

      const response = await sendDraftAPI(sendDraftProperties);

      if (isDraftConflictResult(response)) {
        log.warn(LOG_MESSAGES.SEND_VERSION_CONFLICT, {
          currentDraftVersion: response.currentDraftVersion,
        });
        enterDraftConflict(response.currentDraftVersion);
        return false;
      }

      if (isPayloadTooLargeResult(response)) {
        log.warn(LOG_MESSAGES.SEND_PAYLOAD_TOO_LARGE);
        return false;
      }

      if (response) {
        shouldSkipSaveRef.current = true;
        onSendSuccessRef.current?.();
        deleteDraftFromStore(currentDraftIdRef.current);
        successMessage(t('inbox.userCommunication.actions.sendDraftSuccess'));
        if (window.location.pathname.includes(PATH_SEGMENTS.PREVIEW)) {
          navigateToInboxOrDoBox(navigate, tabParam);
        }
        return true;
      }

      // A failed send may already have moved the server's version.
      draftVersionRef.current = undefined;
      return false;
    } catch (error) {
      log.error(ERROR_MESSAGES.FAILED_TO_SEND, error);
      draftVersionRef.current = undefined;
      errorMessage(t('inbox.userCommunication.actions.sendDraftError'));
      return false;
    } finally {
      if (!isUnmountedRef.current) {
        setIsSendingDraft(false);
      }
    }
  };

  // flushPendingSave closes over the unmemoized saveDraft, so a new identity
  // every render would make this effect's cleanup — which must run only on
  // real unmount — re-fire on every re-render instead. The ref keeps the
  // effect's deps empty while still calling the latest closure.
  const flushPendingSaveRef = useRef(flushPendingSave);
  flushPendingSaveRef.current = flushPendingSave;

  // Deliberately leaves shouldSkipSaveRef alone (that means "sent"/reset) so an
  // in-flight save, and a pending debounce flushed below, still commit to the
  // store after unmount — e.g. closing the DoBox task-details modal by
  // clicking outside must not drop the last 5s of typed text.
  const retryNowRef = useRef(retryNow);
  retryNowRef.current = retryNow;

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === VISIBILITY_VISIBLE) {
        retryNowRef.current();
      }
    };
    const handleOnline = () => retryNowRef.current();
    window.addEventListener(RETRY_TRIGGER_EVENTS.ONLINE, handleOnline);
    document.addEventListener(
      RETRY_TRIGGER_EVENTS.VISIBILITY_CHANGE,
      handleVisibilityChange,
    );
    return () => {
      window.removeEventListener(RETRY_TRIGGER_EVENTS.ONLINE, handleOnline);
      document.removeEventListener(
        RETRY_TRIGGER_EVENTS.VISIBILITY_CHANGE,
        handleVisibilityChange,
      );
    };
  }, []);

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      clearRetryTimer();
      flushPendingSaveRef.current();
    };
  }, []);

  const reset = () => {
    cancelPendingSave();
    setHasStartedComposing(false);
    setCurrentDraftId(undefined);
    pinnedAccountRef.current = undefined;
    setSaveState({ status: SaveStatus.IDLE });
    previousContent.current = initialData;
    lastSaveRequest.current = '';
    lastSaveTimestampRef.current = 0;
    pendingSaveTimestampRef.current = 0;
    shouldSkipSaveRef.current = false;
    isSendingRef.current = false;
    hasUserEditedRecipientsRef.current = false;
    hasUserEditedSubjectRef.current = false;
    editRevisionRef.current = 0;
    pendingSaveRevisionRef.current = 0;
    draftVersionRef.current = undefined;
    hasDraftVersionConflictRef.current = false;
    latchedConflictRef.current = null;
    setDraftConflict(null);
    clearAllTimers();
  };

  const resetDraftId = useCallback(() => {
    setCurrentDraftId(undefined);
    currentDraftIdRef.current = undefined;
    pinnedAccountRef.current = undefined;
    lastSaveRequest.current = '';
    lastSaveTimestampRef.current = Date.now();
    draftVersionRef.current = undefined;
    hasDraftVersionConflictRef.current = false;
    latchedConflictRef.current = null;
    setDraftConflict(null);
    setSaveState({ status: SaveStatus.IDLE });
    log.debug(LOG_MESSAGES.DRAFT_ID_RESET);
  }, []);

  const detachDraft = useCallback((): DetachedDraft | undefined => {
    const draftId = currentDraftIdRef.current;
    if (!draftId) return undefined;
    const detached: DetachedDraft = {
      draftId,
      pinnedAccount: pinnedAccountRef.current,
      draftVersion: draftVersionRef.current,
      hasDraftVersionConflict: hasDraftVersionConflictRef.current,
      serverDraftVersion: draftConflictRef.current?.serverDraftVersion,
      isDeletedOnProvider: draftConflictRef.current?.isDeletedOnProvider,
      lastSaveRequest: lastSaveRequest.current,
    };
    resetDraftId();
    return detached;
  }, [resetDraftId]);

  const setWriteLineage = useCallback((lineageId: string) => {
    writeLineageRef.current = lineageId;
  }, []);

  const setWriteLineageIfAbsent = useCallback((lineageId: string) => {
    writeLineageRef.current ??= lineageId;
  }, []);

  const seedDraftVersion = useCallback((version: string) => {
    draftVersionRef.current = version;
  }, []);

  // Content stays untouched: the id lands the way a finished create does, so later saves update this draft.
  const adoptSyncedDraft = useCallback(
    (syncedDraftId: string, syncedVersion?: string) => {
      if (syncedVersion) {
        draftVersionRef.current = syncedVersion;
      }
      if (currentDraftIdRef.current) {
        return;
      }
      currentDraftIdRef.current = syncedDraftId;
      // A compose closed while it waited still saves behind the hold: it needs the id, not the state.
      if (isUnmountedRef.current) {
        return;
      }
      setCurrentDraftId(syncedDraftId);
      onDraftCreatedRef.current?.(syncedDraftId);
    },
    [],
  );

  const restoreDraft = useCallback((detached: DetachedDraft) => {
    currentDraftIdRef.current = detached.draftId;
    setCurrentDraftId(detached.draftId);
    pinnedAccountRef.current = detached.pinnedAccount;
    draftVersionRef.current = detached.draftVersion;
    hasDraftVersionConflictRef.current = detached.hasDraftVersionConflict;
    latchedConflictRef.current = detached.hasDraftVersionConflict
      ? {
          serverDraftVersion: detached.serverDraftVersion,
          isDeletedOnProvider: detached.isDeletedOnProvider,
        }
      : null;
    setDraftConflict(
      detached.hasDraftVersionConflict
        ? {
            serverDraftVersion: detached.serverDraftVersion,
            isDeletedOnProvider: detached.isDeletedOnProvider,
          }
        : null,
    );
    lastSaveRequest.current = detached.lastSaveRequest;
  }, []);

  return {
    hasStartedComposing,
    draftId: currentDraftId,
    saveState,
    reset,
    resetDraftId,
    saveDraft,
    waitForInFlightSave,
    getCurrentDraftId,
    getPinnedAccount,
    isDraftInOtherAccount,
    detachDraft,
    restoreDraft,
    adoptSyncedDraft,
    holdSavesUntil,
    seedDraftVersion,
    setWriteLineage,
    setWriteLineageIfAbsent,
    hasVersionConflict,
    getLatchedConflict,
    draftConflict,
    enterDraftConflict,
    enterDraftDeleted,
    resolveDraftConflict,
    hasScheduledSave,
    hasPendingSave,
    flushPendingSave,
    isSavingDraft,
    sendDraftEmail,
    isSendingDraft,
    autoSaveOnChange,
    autoSaveText,
    autoSaveSubject,
    autoSaveRecipients,
    autoSavePaste,
    isDraftWorthy,
    isDirty,
    markDirty,
    markClean,
    markContentSaved,
    isInitialLoadComplete,
    cancelPendingSave,
    isSendingRef,
    setSaveState,
    markDraftDiscarded,
    recordRemovedAttachment,
    getAttachmentsToRemove,
  };
}
